// src/Redux/slices/ownerSlice.js
// Platform-owner state: the list of companies and the company the owner is "working on" right now.
// The selected company is sent with every API call (X-Owner-CompanyId) – it decides whose data a form shows and saves.
// It has NOTHING to do with the sidebar: the owner's sidebar always belongs to the owner's own company.

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api, { SELECTED_COMPANY_KEY } from '../api/axiosInstance';

const toId = (v) => (v != null && v !== '' ? String(v) : null);
const msg = (err) => err.response?.data?.message || err.message;

const savedCompanyId = (() => {
  try {
    return toId(localStorage.getItem(SELECTED_COMPANY_KEY));
  } catch {
    return null;
  }
})();

// ── Thunks ───────────────────────────────────────────────────────────────────
export const fetchAllCompanies = createAsyncThunk('owner/fetchCompanies', async (_, { rejectWithValue }) => {
  try {
    const { data } = await api.get('Owner/companies');
    return data.data;
  } catch (err) {
    return rejectWithValue(msg(err));
  }
});

export const createCompany = createAsyncThunk('owner/createCompany', async (dto, { rejectWithValue }) => {
  try {
    const { data } = await api.post('Owner/companies', dto);
    return data.data;
  } catch (err) {
    return rejectWithValue(msg(err));
  }
});

export const updateCompany = createAsyncThunk('owner/updateCompany', async ({ id, dto }, { rejectWithValue }) => {
  try {
    const { data } = await api.put(`Owner/companies/${id}`, dto);
    return data.data;
  } catch (err) {
    return rejectWithValue(msg(err));
  }
});

export const toggleCompanyStatus = createAsyncThunk('owner/toggleCompanyStatus', async ({ id, active }, { dispatch, rejectWithValue }) => {
  try {
    await api.patch(`Owner/companies/${id}/status?active=${active}`);
    await dispatch(fetchAllCompanies());
    return { id, active };
  } catch (err) {
    return rejectWithValue(msg(err));
  }
});

export const extendSubscription = createAsyncThunk('owner/extendSubscription', async ({ id, days, newEndDate }, { dispatch, rejectWithValue }) => {
  try {
    await api.post(`Owner/companies/${id}/extend`, { days, newEndDate });
    await dispatch(fetchAllCompanies());
    return id;
  } catch (err) {
    return rejectWithValue(msg(err));
  }
});

// ── Slice ─────────────────────────────────────────────────────────────────────
const ownerSlice = createSlice({
  name: 'owner',
  initialState: {
    companies: [],
    selectedCompanyId: savedCompanyId,
    loading: false,
    actionLoading: false,
    error: null
  },
  reducers: {
    selectCompany: (state, action) => {
      state.selectedCompanyId = toId(action.payload);
      try {
        if (state.selectedCompanyId) localStorage.setItem(SELECTED_COMPANY_KEY, state.selectedCompanyId);
        else localStorage.removeItem(SELECTED_COMPANY_KEY);
      } catch {
        /* ignore */
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllCompanies.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(fetchAllCompanies.fulfilled, (s, a) => {
        s.loading = false;
        s.companies = a.payload || [];
        const exists = s.companies.some((c) => toId(c.companyId) === s.selectedCompanyId);
        if (!exists) {
          // default = the owner's own company
          const own = s.companies.find((c) => c.isOwnerCompany) || s.companies[0];
          s.selectedCompanyId = own ? toId(own.companyId) : null;
          try {
            if (s.selectedCompanyId) localStorage.setItem(SELECTED_COMPANY_KEY, s.selectedCompanyId);
          } catch {
            /* ignore */
          }
        }
      })
      .addCase(fetchAllCompanies.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload;
      })
      .addCase(createCompany.pending, (s) => {
        s.actionLoading = true;
      })
      .addCase(createCompany.fulfilled, (s, a) => {
        s.actionLoading = false;
        if (a.payload) s.companies = [...s.companies, a.payload];
      })
      .addCase(createCompany.rejected, (s) => {
        s.actionLoading = false;
      })
      .addCase(updateCompany.pending, (s) => {
        s.actionLoading = true;
      })
      .addCase(updateCompany.fulfilled, (s, a) => {
        s.actionLoading = false;
        if (a.payload) s.companies = s.companies.map((c) => (toId(c.companyId) === toId(a.payload.companyId) ? a.payload : c));
      })
      .addCase(updateCompany.rejected, (s) => {
        s.actionLoading = false;
      });
  }
});

export const { selectCompany } = ownerSlice.actions;
export default ownerSlice.reducer;

// ── Selectors ─────────────────────────────────────────────────────────────────
export const selectOwnerCompanies = (state) => state.owner.companies;
export const selectSelectedCompanyId = (state) => state.owner.selectedCompanyId;
export const selectOwnerLoading = (state) => state.owner.loading;
export const selectOwnerActionLoading = (state) => state.owner.actionLoading;

/** The company whose data is on screen: the owner's pick, everybody else: their own company. */
export const selectEffectiveCompanyId = (state) => {
  if (state.auth?.isOwner) return state.owner.selectedCompanyId || String(state.auth?.user?.companyId || '');
  return String(state.auth?.user?.companyId || '');
};

export const selectSelectedCompany = (state) => {
  const id = state.owner.selectedCompanyId;
  return id ? state.owner.companies.find((c) => toId(c.companyId) === id) || null : null;
};
